import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import OrderCard from '../../components/OrderCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import './User.css';

export default function Orders() {
  const { api, user, setOrdersBadge } = useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const role = user?.role?.toLowerCase();
  const isAdminOrRider = role === 'admin' || role === 'rider';

  useEffect(() => {
    fetchOrders();
    
    // Clear orders badge when viewing orders
    if (typeof setOrdersBadge === 'function') {
      setOrdersBadge(0);
    }
  }, []);

  const fetchOrders = async () => {
    try {
      setError('');

      let endpoint = '/orders/my-orders';
      
      if (isAdminOrRider) {
        // Try different endpoints for admin/rider
        const endpoints = ['/orders', '/orders/all-orders', '/orders/all'];
        
        for (const ep of endpoints) {
          try {
            const response = await api.get(ep);
            const data = Array.isArray(response.data) 
              ? response.data 
              : (response.data?.orders || response.data?.data || []);
            
            setOrders(data);
            return;
          } catch (e) {
            continue;
          }
        }
        
        throw new Error('No valid orders endpoint found');
      } else {
        const response = await api.get(endpoint);
        const data = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.orders || []);
        
        setOrders(data);
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
  };

  // Filter running orders for regular users
  const displayOrders = isAdminOrRider 
    ? orders 
    : orders.filter(order => {
        const status = order.status?.toLowerCase();
        return ['pending', 'confirmed', 'processing', 'out-for-delivery'].includes(status);
      });

  if (loading) {
    return <LoadingSpinner message="Loading orders..." />;
  }

  if (displayOrders.length === 0) {
    return (
      <div className="user-orders">
        <div className="container">
          <h1 className="page-title">
            <span className="title-icon">📦</span>
            {isAdminOrRider ? 'All Orders' : 'My Orders'}
          </h1>

          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h2 className="empty-state-title">
              {isAdminOrRider ? 'No orders found' : 'No active orders'}
            </h2>
            <p className="empty-state-text">
              {isAdminOrRider 
                ? 'All orders will appear here'
                : 'Your active orders will appear here'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-orders">
      <div className="container">
        <div className="orders-header">
          <h1 className="page-title">
            <span className="title-icon">📦</span>
            {isAdminOrRider ? 'All Orders' : 'My Orders'}
          </h1>
          <button
            className="btn btn-outline btn-sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        <div className="orders-count">
          <span className="count-badge">{displayOrders.length} Orders</span>
        </div>

        <div className="orders-list">
          {displayOrders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      </div>
    </div>
  );
}